// Reconciling review marks with GitHub's per-file "Viewed" checkbox
// (docs/design.md, "GitHub's Viewed checkbox"). One file at a time, from
// what both sides last agreed on (its baseline) and what each says now:
//
// - A change here since the baseline (the file became fully reviewed, or
//   stopped being) is sent to GitHub: viewed when every change is reviewed,
//   unviewed when one is unmarked. It wins over a change on GitHub since the
//   baseline, so an unmark here always reaches GitHub.
// - Otherwise a change on GitHub since the baseline to VIEWED marks the
//   whole file here. UNVIEWED and DISMISSED import nothing.
// - GitHub's answer is ignored for a file while a write to it is waiting or
//   in flight, and when it was read before the last write to that file
//   landed (`Baseline.at`): it predates the write.
//
// Without a baseline for the head, both sides count as having agreed on
// "not reviewed, not viewed".
//
// GitHub's markFileAsViewed names no head: it ticks the file at whatever
// head the pull request has when it lands. So a write decided at one head
// can tick the next one, over changes nobody reviewed. Hence: at a head
// other than the stored baseline's, a VIEWED is not imported when the
// file's diff (its base and head blobs) changed since the baseline, or
// either is unknown. It is agreed on instead, so a later read of it imports
// nothing either; a VIEWED read after GitHub said UNVIEWED or DISMISSED at
// the new head is the viewer's own, and is imported.
//
// A mark whose outcome is unknown (its answer was lost, or said no head)
// leaves its baseline `uncertain`. A read at its head that started after
// it settles the doubt (and a reviewed file whose mark did not land is
// marked again); until then older reads are not used. At another head
// where the file changed and is not reviewed here, a VIEWED may be that
// mark, so it is unmarked there.

/** GitHub's `FileViewedState`. DISMISSED: viewed, then changed by a push. */
export type ViewedState = 'VIEWED' | 'UNVIEWED' | 'DISMISSED';

/** What both sides of one file last agreed on. */
export interface Baseline {
  /** The pull request head the agreement is about. */
  head: string;
  /** Every change of the file was reviewed here. */
  local: boolean;
  /** GitHub's state for the viewer. */
  remote: ViewedState;
  /** When the last write to GitHub for this file landed (epoch ms; 0: never). Reads started before it are stale. */
  at: number;
  /** The file's diff at `head` (base and head blobs), when known: a VIEWED at another head is imported only for the same diff. */
  diff?: string | null;
  /** A mark was sent at `head` whose outcome is unknown: it may have landed there, or at a later head. */
  uncertain?: boolean;
}

/** What each side says about one file now. */
export interface Observation {
  head: string;
  /** Every change is reviewed here; null while unknown. */
  local: boolean | null;
  /** GitHub's state, read at `remoteAt`; undefined while unknown at this head. */
  remote: ViewedState | undefined;
  /** When the read of `remote` started (epoch ms). */
  remoteAt: number;
  /** A write to GitHub for this file is waiting or in flight. */
  writing: boolean;
  /** The file's diff at `head` (an identity of its base and head blobs); null when unknown. */
  diff: string | null;
}

export type Decision =
  /** Nothing to do, nothing to record. */
  | { kind: 'none' }
  /** The sides agree on something new: record `baseline`. */
  | { kind: 'settle'; baseline: Baseline }
  /** Set GitHub's checkbox; record `baseline` (with the write's time) once the write lands. */
  | { kind: 'push'; viewed: boolean; baseline: Baseline }
  /** Mark every change of the file here; then record `baseline`. */
  | { kind: 'import'; baseline: Baseline };

/** The agreement assumed at a head with no baseline. */
export function initialBaseline(head: string, at = 0, diff?: string | null): Baseline {
  return diff == null ? { head, local: false, remote: 'UNVIEWED', at } : { head, local: false, remote: 'UNVIEWED', at, diff };
}

/** The baseline in force for `head`: the stored one when it is for that head, else the initial one (keeping when the file was last written). */
export function baselineAt(b: Baseline | undefined, head: string, diff?: string | null): Baseline {
  return b && b.head === head ? b : initialBaseline(head, b?.at ?? 0, diff);
}

/** At `o.head`, a VIEWED may be a write of ours decided at the stored baseline's head: the head differs and the file's diff is not known to be the same. */
function maybeMisplaced(stored: Baseline | undefined, o: Observation): boolean {
  return !!stored && stored.head !== o.head && !(o.diff && stored.diff === o.diff);
}

/** What to do about one file (see the file comment). */
export function decide(stored: Baseline | undefined, o: Observation): Decision {
  if (o.local === null || o.remote === undefined) return { kind: 'none' };
  const fresh = baselineAt(stored, o.head, o.diff);
  let base = o.diff && fresh.diff !== o.diff ? { ...fresh, diff: o.diff } : fresh;
  const freshRead = !o.writing && o.remoteAt >= base.at;
  if (base.uncertain) {
    // Doubt about a mark at this head: only a read that started after it settles it.
    if (!o.writing && !freshRead) return { kind: 'none' };
    if (freshRead) {
      const { uncertain: _, ...settled } = base;
      base = settled;
    }
  }
  const remote = freshRead ? o.remote : base.remote;
  const local = o.local;
  // A mark of ours, outcome unknown, may have ticked this head over changes not reviewed here.
  if (stored?.uncertain && maybeMisplaced(stored, o) && freshRead && remote === 'VIEWED' && !local)
    return { kind: 'push', viewed: false, baseline: { ...base, local, remote: 'UNVIEWED' } };
  const agreed = (r: ViewedState): Decision =>
    !stored || stored.head !== o.head || local !== stored.local || r !== stored.remote || !!stored.uncertain !== !!base.uncertain
      ? { kind: 'settle', baseline: { ...base, local, remote: r } }
      : { kind: 'none' };
  if (local !== base.local) {
    if (local) return remote === 'VIEWED' ? agreed('VIEWED') : { kind: 'push', viewed: true, baseline: { ...base, local, remote: 'VIEWED' } };
    // An unmark here wins over GitHub, whatever happened there.
    return remote === 'VIEWED' ? { kind: 'push', viewed: false, baseline: { ...base, local, remote: 'UNVIEWED' } } : agreed(remote);
  }
  if (remote !== base.remote) {
    if (remote === 'VIEWED' && !local && !maybeMisplaced(stored, o)) return { kind: 'import', baseline: { ...base, local: true, remote } };
    return agreed(remote);
  }
  // A fresh read at a head the stored baseline is not about: it is this head's agreement now.
  if (stored && (stored.head !== o.head || stored.uncertain) && freshRead) return agreed(remote);
  return { kind: 'none' };
}
